// Agents vocaux › Numéros — /voice-numbers. The account's phone numbers:
// bought here (one monthly subscription per number, taken from the numbers
// Unanimity keeps routed to the platform — nobody types a number), the voice
// agent answering each one, cancellation, invoices.
// Zones: header (scope, « Ajouter un numéro ») → Stripe return / pending
// payment banners → « À vérifier » (failed payment, number no agent answers,
// line silent for days) → state tiles → views: numbers | invoices.
// URL (backend + Stripe redirects, /parameters?tab=voice forwards here):
// ?buy=1 opens the purchase, ?voice_number=paid&session_id=… settles a
// checkout, ?voice_number=cancelled says nothing was charged. ?view=invoices.
// API: GET /api/voice-numbers, GET /api/voice-agents, POST
// /api/voice-numbers/checkout/confirm, GET /api/voice-numbers/invoices (admins,
// on the invoices view); row actions in NumberCard.js, purchase in BuyNumber.js.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileDown, Headphones, Phone, PhoneCall, Plus, Receipt, Wallet } from 'lucide-react';
import {
    Badge, Banner, Card, EmptyState, Kpi, PageHeader, Tabs, Toast,
} from '../../design/ui';
import BuyNumber from './BuyNumber';
import NumberCard from './NumberCard';
import {
    INVOICE_STATUS_LABELS, canManage, confirmCheckout, fail, formatDay, formatPrice, getInvoices, getNumbers, listAgents,
} from './api';
import { formatRelative } from '../../utils/voiceFormat';
import { numbersAttention, numbersFigures } from './voiceModel';
import { AttentionCard, formatCount, pluralT, scrollToId } from './VoiceKit';
import { numberLabel } from './phoneFormat';
import './VoiceSetup.css';

const POLL_MS = 3000;
const POLL_TRIES = 5;

const INVOICE_TONES = { paid: 'success', open: 'warning', uncollectible: 'danger' };

const Invoices = ({ t, lang }) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        getInvoices()
            .then((res) => alive && setRows(res.data.invoices || []))
            .catch((err) => {
                if (!alive) return;
                setError(fail(err, t('voiceSetup.invoices.error', 'Les factures sont indisponibles pour le moment.')));
                setRows([]);
            });
        return () => { alive = false; };
    }, [t]);

    return (
        <Card
            icon={Receipt}
            title={t('voiceSetup.invoices.title', 'Factures')}
            description={t('voiceUi.invoices.desc', 'Une facture par échéance de vos numéros, réglée par carte sur Stripe.')}
            flush
        >
            {rows === null && (
                <div className="voice-v2__list-skeleton voice-v2__list-skeleton--inset" aria-busy="true">
                    {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
                </div>
            )}
            {error && <div className="voice-v2__inset"><Banner tone="danger">{error}</Banner></div>}
            {rows && !error && rows.length === 0 && (
                <EmptyState icon={Receipt} title={t('voiceSetup.invoices.none', 'Aucune facture pour l’instant.')}>
                    {t('voiceUi.invoices.noneHint', 'La première arrive avec le paiement d’un numéro.')}
                </EmptyState>
            )}
            {rows && rows.length > 0 && (
                <div className="u-table-scroll">
                    <table className="u-table voice-v2__invoices">
                        <thead>
                            <tr>
                                <th style={{ width: '24%' }}>{t('voiceUi.invoices.colDate', 'Date')}</th>
                                <th style={{ width: '22%' }}>{t('voiceUi.invoices.colNumber', 'Facture')}</th>
                                <th style={{ width: '16%' }}>{t('voiceUi.invoices.colAmount', 'Montant')}</th>
                                <th style={{ width: '16%' }}>{t('voiceUi.invoices.colStatus', 'Statut')}</th>
                                <th style={{ width: '22%' }} aria-label={t('voiceUi.invoices.colActions', 'Actions')} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{formatDay(row.created_at, lang)}</td>
                                    <td title={row.number || ''}>{row.number ? <span className="u-code">{row.number}</span> : '—'}</td>
                                    <td className="u-num">{formatPrice(row.amount_eur, lang)}</td>
                                    <td>
                                        <Badge tone={INVOICE_TONES[row.status]} dot>
                                            {t(`voiceSetup.invoices.status.${row.status}`, INVOICE_STATUS_LABELS[row.status] || row.status || '')}
                                        </Badge>
                                    </td>
                                    <td>
                                        <span className="voice-v2__invoice-actions">
                                            {row.hosted_invoice_url && (
                                                <a className="u-btn u-btn--ghost u-btn--sm" href={row.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                                                    {row.status === 'open' ? t('voiceSetup.invoices.pay', 'Régler') : t('voiceSetup.invoices.view', 'Voir')}
                                                    <ExternalLink aria-hidden="true" />
                                                </a>
                                            )}
                                            {row.invoice_pdf && (
                                                <a
                                                    className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                                                    href={row.invoice_pdf}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={t('voiceUi.invoices.pdf', 'Télécharger le PDF')}
                                                    title={t('voiceUi.invoices.pdf', 'Télécharger le PDF')}
                                                >
                                                    <FileDown aria-hidden="true" />
                                                </a>
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

const VoiceNumbers = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n?.language || 'fr';
    const tp = pluralT(t);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState(null);
    const [agents, setAgents] = useState([]);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);
    const [banner, setBanner] = useState(null);
    const [buying, setBuying] = useState(searchParams.get('buy') === '1');
    const manage = canManage();
    const handledReturn = useRef(false);

    const load = useCallback(async () => {
        try {
            const [numbersRes, agentsRes] = await Promise.all([getNumbers(), listAgents()]);
            setView(numbersRes.data);
            setAgents(agentsRes.data.agents || []);
            setError('');
            return numbersRes.data;
        } catch (err) {
            setError(fail(err, t('voiceSetup.loadError', 'Les numéros n’ont pas pu être chargés. Rechargez la page.')));
            return null;
        }
    }, [t]);

    // Back from Stripe: settle the checkout now (the sweep would within minutes).
    useEffect(() => {
        if (handledReturn.current) return;
        handledReturn.current = true;
        const outcome = searchParams.get('voice_number');
        const sessionId = searchParams.get('session_id');
        const clean = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('voice_number');
            next.delete('session_id');
            next.delete('buy');
            setSearchParams(next, { replace: true });
        };
        if (outcome === 'cancelled') {
            setBanner({ tone: 'neutral', text: t('voiceSetup.return.cancelled', 'Paiement annulé : aucun montant n’a été débité.') });
            clean();
        }
        if (outcome !== 'paid' || !sessionId) {
            load();
            return;
        }
        (async () => {
            setBanner({ tone: 'info', text: t('voiceSetup.return.checking', 'Paiement reçu, activation du numéro…') });
            let status = null;
            let number = '';
            try {
                const res = await confirmCheckout(sessionId);
                status = res.data.status;
                number = numberLabel((res.data.numbers || []).find((n) => n.phone_number === res.data.phone_number));
                setView(res.data);
                const agentsRes = await listAgents();
                setAgents(agentsRes.data.agents || []);
            } catch (err) {
                await load();
            }
            for (let i = 0; i < POLL_TRIES && (status === 'pending' || status === 'fulfilling'); i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
                // eslint-disable-next-line no-await-in-loop
                const fresh = await load();
                status = fresh?.pending_checkout ? 'pending' : 'active';
            }
            if (status === 'active' || status === 'canceling') {
                setBanner({
                    tone: 'success',
                    text: number
                        ? t('voiceSetup.return.active', 'Votre numéro {{number}} est actif.', { number })
                        : t('voiceSetup.return.activeShort', 'Votre numéro est actif.'),
                });
            } else if (status === 'paid_unassigned') {
                setBanner({ tone: 'warning', text: t('voiceSetup.return.unassigned', 'Paiement validé. Votre numéro vous sera attribué dès qu’un nouveau lot est disponible : vous serez notifié.') });
            } else {
                setBanner({ tone: 'info', text: t('voiceSetup.return.pending', 'Le paiement est en cours de validation : le numéro apparaîtra ici dans quelques minutes.') });
            }
            clean();
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const numbers = view?.numbers || [];
    const offer = view?.offer || {};
    const pending = view?.pending_checkout;
    const showInvoices = Boolean(manage && view?.has_billing);
    const tab = showInvoices && searchParams.get('view') === 'invoices' ? 'invoices' : 'numbers';
    const figures = numbersFigures(view);

    const setTab = (next) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (next === 'invoices') params.set('view', 'invoices'); else params.delete('view');
            return params;
        }, { replace: true });
    };

    const openBuy = () => setBuying(true);
    const closeBuy = () => {
        setBuying(false);
        if (searchParams.get('buy')) {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete('buy');
                return params;
            }, { replace: true });
        }
    };

    const rowId = (item) => `voice-number-${item.phone_number.replace(/\W/g, '')}`;
    const attention = numbersAttention(view).map((item) => {
        const number = numberLabel(item.number);
        if (item.kind === 'pastDue') {
            return {
                ...item,
                title: t('voiceUi.numbers.attPastDue', 'Le paiement du {{number}} a échoué', { number }),
                detail: t('voiceUi.numbers.attPastDueDetail', 'Réglez la facture en attente pour garder ce numéro.'),
                action: showInvoices ? { label: t('voiceUi.numbers.seeInvoices', 'Voir les factures'), onClick: () => setTab('invoices') } : null,
            };
        }
        if (item.kind === 'noAgent') {
            return {
                ...item,
                title: t('voiceUi.numbers.attNoAgent', 'Le {{number}} ne répond pas', { number }),
                detail: item.gone
                    ? t('voiceSetup.agentGone', 'Ce numéro renvoyait vers un agent qui n’est pas un agent vocal de ce compte : choisissez-en un.')
                    : t('voiceUi.numbers.attNoAgentDetail', 'Aucun agent vocal n’y est relié : les appels ne sont pas décrochés.'),
                action: manage ? {
                    label: t('voiceUi.numbers.chooseAgent', 'Choisir l’agent'),
                    onClick: () => { setTab('numbers'); setTimeout(() => scrollToId(rowId(item.number)), 0); },
                } : null,
            };
        }
        return {
            ...item,
            title: t('voiceUi.numbers.attSilent', 'Aucun appel sur le {{number}} depuis le {{date}}', { number, date: formatDay(item.last, lang) }),
            detail: tp(
                'voiceUi.numbers.attSilentDetail',
                '{{count}} jour sans appel : appelez ce numéro pour vérifier que {{agent}} décroche.',
                '{{count}} jours sans appel : appelez ce numéro pour vérifier que {{agent}} décroche.',
                { count: item.days, agent: item.number.agent?.name || '' },
            ),
            action: { label: t('voiceUi.numbers.seeCalls', 'Voir les appels'), onClick: () => navigate('/voice-tickets') },
        };
    });

    let subtitle = t('voiceSetup.numbers.pageSubtitle', 'Les numéros que vos clients appellent, et l’agent vocal qui décroche sur chacun.');
    if (view && numbers.length) {
        subtitle = [
            tp('voiceUi.numbers.scope', '{{count}} numéro', '{{count}} numéros', { count: numbers.length }),
            tp('voiceUi.numbers.scopeAnswering', '{{count}} relié à un agent', '{{count}} reliés à un agent', { count: figures.answering }),
            t('voiceUi.numbers.scopePrice', '{{price}} par numéro et par mois, sans engagement', { price: formatPrice(offer.monthly_price_eur, lang) }),
        ].join(' · ');
    }

    const renderKpis = () => {
        if (!view) {
            if (error) return null;
            return (
                <div className="u-kpi-row" aria-hidden="true">
                    {[0, 1, 2].map((i) => <span key={i} className="u-skeleton voice-v2__kpi-skeleton" />)}
                </div>
            );
        }
        if (!numbers.length) return null;
        return (
            <div className="u-kpi-row">
                <Kpi
                    icon={Phone}
                    tone={figures.withoutAgent ? 'warning' : 'success'}
                    label={t('voiceUi.numbers.kpiNumbers', 'Numéros')}
                    value={formatCount(figures.total, lang)}
                    context={figures.withoutAgent
                        ? tp('voiceUi.numbers.kpiWithoutAgent', '{{count}} ne répond pas', '{{count}} ne répondent pas', { count: figures.withoutAgent })
                        : t('voiceUi.numbers.kpiAllAnswer', 'chacun relié à un agent')}
                    contextTone={figures.withoutAgent ? 'warning' : undefined}
                    meter={{ value: figures.answering, max: figures.total, tone: figures.withoutAgent ? 'warning' : 'success', label: t('voiceUi.numbers.kpiNumbers', 'Numéros') }}
                    active={tab === 'numbers'}
                    onClick={() => setTab('numbers')}
                />
                <Kpi
                    icon={PhoneCall}
                    tone="info"
                    label={t('voiceUi.agents.kpiCalls', 'Appels sur 30 jours')}
                    value={formatCount(figures.calls30d, lang)}
                    context={figures.lastCall
                        ? t('voiceUi.lastCall', 'dernier : {{when}}', { when: formatRelative(figures.lastCall) })
                        : t('voiceUi.noCallYet', 'aucun appel reçu')}
                    onClick={() => navigate('/voice-tickets')}
                    title={t('voiceUi.openCalls', 'Ouvrir les appels')}
                />
                {figures.subscriptions > 0 && (
                    <Kpi
                        icon={Wallet}
                        label={t('voiceUi.numbers.kpiMonthly', 'Abonnements')}
                        value={formatPrice(figures.monthly, lang)}
                        unit={t('voiceUi.numbers.perMonth', '/ mois')}
                        context={figures.nextRenewal
                            ? tp('voiceUi.numbers.kpiMonthlyContext', '{{count}} numéro · prochaine échéance le {{date}}', '{{count}} numéros · prochaine échéance le {{date}}', { count: figures.subscriptions, date: formatDay(figures.nextRenewal, lang) })
                            : tp('voiceUi.numbers.kpiMonthlyCount', '{{count}} numéro facturé', '{{count}} numéros facturés', { count: figures.subscriptions })}
                        active={tab === 'invoices'}
                        onClick={showInvoices ? () => setTab('invoices') : undefined}
                        title={showInvoices ? t('voiceUi.numbers.seeInvoices', 'Voir les factures') : undefined}
                    />
                )}
            </div>
        );
    };

    const renderNumbers = () => {
        if (!view) {
            if (error) return null;
            return (
                <section className="u-card voice-v2__list-skeleton" aria-busy="true">
                    {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
                </section>
            );
        }
        if (!numbers.length) {
            return (
                <section className="u-card">
                    <EmptyState
                        icon={Phone}
                        title={t('voiceSetup.empty.title', 'Pas encore de numéro')}
                        action={manage ? (
                            <button type="button" className="u-btn u-btn--primary" onClick={openBuy}>
                                <Plus aria-hidden="true" />
                                {t('voiceSetup.empty.cta', 'Choisir un numéro')}
                            </button>
                        ) : undefined}
                    >
                        <p className="voice-v2__empty-text">
                            {t('voiceSetup.empty.text', 'Choisissez un numéro et l’agent qui y répond : vos clients l’appellent pour suivre un colis ou ouvrir une demande, l’agent décroche 24 h/24.')}
                        </p>
                        {agents.length === 0 && (
                            <p className="voice-v2__empty-text">
                                {t('voiceUi.numbers.emptyNoAgent', 'Vous n’avez pas encore d’agent vocal : vous pourrez relier le numéro plus tard.')}{' '}
                                <Link className="u-link" to="/voice-agents">{t('voiceSetup.createAgentFirst', 'Créez d’abord un agent vocal')}</Link>
                            </p>
                        )}
                    </EmptyState>
                </section>
            );
        }
        return (
            <Card
                icon={Phone}
                title={t('voiceSetup.numbers.title', 'Vos numéros')}
                description={t('voiceSetup.numbers.desc', '{{price}} par numéro et par mois, sans engagement. Un numéro, un agent qui répond.', {
                    price: formatPrice(offer.monthly_price_eur, lang),
                })}
                flush
            >
                <ul className="u-list">
                    {numbers.map((item) => (
                        <NumberCard
                            key={item.phone_number}
                            item={item}
                            agents={agents}
                            manage={manage}
                            onChange={(data) => { setView(data); setError(''); }}
                            onError={(text) => setToast({ tone: 'danger', text })}
                        />
                    ))}
                </ul>
            </Card>
        );
    };

    return (
        <div className="u-page voice-v2">
            <PageHeader
                title={t('voiceSetup.numbers.pageTitle', 'Numéros de téléphone')}
                subtitle={subtitle}
                actions={(
                    <>
                        <Link className="u-btn u-btn--secondary" to="/voice-agents">
                            <Headphones aria-hidden="true" />
                            {t('voiceUi.numbers.agentsLink', 'Agents vocaux')}
                        </Link>
                        {manage && numbers.length > 0 && (
                            <button type="button" className="u-btn u-btn--primary" onClick={openBuy}>
                                <Plus aria-hidden="true" />
                                {t('voiceSetup.addNumber', 'Ajouter un numéro')}
                            </button>
                        )}
                    </>
                )}
            />

            <div className="u-well">
                {banner && <Banner tone={banner.tone}>{banner.text}</Banner>}
                {error && <Banner tone="danger">{error}</Banner>}
                {view && !manage && (
                    <Banner tone="neutral">{t('voiceSetup.adminOnly', 'Seuls les administrateurs du compte achètent, routent et résilient les numéros.')}</Banner>
                )}
                {pending && (
                    <Banner tone="info">
                        {pending.status === 'fulfilling'
                            ? t('voiceSetup.pending.fulfilling', 'Paiement validé : activation du {{number}} en cours.', { number: numberLabel(pending) })
                            : t('voiceSetup.pending.open', 'Paiement en attente pour le {{number}}, réservé pour vous pendant le paiement. Page de paiement fermée ? Recommencez : rien n’a été débité.', { number: numberLabel(pending) })}
                    </Banner>
                )}
                {view?.paid_unassigned > 0 && (
                    <Banner tone="warning">
                        {t('voiceSetup.unassigned', 'Paiement validé : votre numéro vous sera attribué dès qu’un nouveau lot est disponible.')}
                    </Banner>
                )}
                <AttentionCard items={attention} t={t} />
                {renderKpis()}
                {showInvoices && (
                    <Tabs
                        label={t('voiceSetup.numbers.pageTitle', 'Numéros de téléphone')}
                        active={tab}
                        onChange={setTab}
                        tabs={[
                            { key: 'numbers', label: t('voiceUi.numbers.tabNumbers', 'Numéros'), icon: Phone, count: formatCount(numbers.length, lang) },
                            { key: 'invoices', label: t('voiceSetup.invoices.title', 'Factures'), icon: Receipt },
                        ]}
                    />
                )}
                <div role={showInvoices ? 'tabpanel' : undefined}>
                    {tab === 'invoices' ? <Invoices t={t} lang={lang} /> : renderNumbers()}
                </div>
            </div>

            {buying && view && (
                <BuyNumber offer={offer} agents={agents} defaultAgentId={agents[0]?.id} onCancel={closeBuy} />
            )}
            {toast && (
                <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('voiceUi.close', 'Fermer')}>
                    {toast.text}
                </Toast>
            )}
        </div>
    );
};

export default VoiceNumbers;
